import type { ReactNode } from 'react';
import clsx from 'clsx';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@monksflow/monks-ui';
import { mergeClasses } from '@/shared/lib/merge-classes';

interface FormSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the panel, e.g. "Edit project", "Project members". */
  title: string;
  /** Omit where the title alone says it. */
  description?: string;
  /** The confirming row. Lay it out as the panel needs; this only positions it. */
  footer?: ReactNode;
  /** Drop the X while work is in flight, so the only way out is one the panel controls. */
  showCloseButton?: boolean;
  /**
   * Rule off the header and the footer and let the body scroll between them.
   * For a panel whose content has no upper bound, where the two fixed rows have
   * to stay legible against a list running under them.
   */
  dividers?: boolean;
  /**
   * Drop the body's own inset and let the children carry it. For a body that is
   * itself divided, where a column's rule has to meet the header's and the
   * footer's rather than stop short of them.
   */
  flushBody?: boolean;
  /** 480px instead of 640px, for a body that is one column of short rows. */
  narrow?: boolean;
  /**
   * Merged over the panel's own classes, so a `w-*` or `gap-*` here replaces the panel's rather
   * than sitting beside it.
   */
  className?: string;
  children: ReactNode;
}

/**
 * Right-hand panel the redesign uses wherever the old design opened a centred
 * modal.
 *
 * It exists for the geometry. Three surfaces need the same 640px panel, and the
 * width, the inset and the single rounded edge are exactly the values that drift
 * when each one writes them out again. `sm:max-w-none` is load-bearing: the
 * design system clamps `SheetContent` to 384px above that breakpoint, so without
 * it the panel silently renders at well under half its width.
 *
 * The body is a plain scrolling div because the design system ships no
 * `SheetBody` to pair with `DialogBody`.
 */
export function FormSidePanel(props: FormSidePanelProps) {
  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent
        side="right"
        data-testid="form-side-panel"
        // `DialogContent` detects a missing description among its children and
        // injects this itself, which is why thirteen dialog call sites never
        // pass it. `SheetContent` has no such logic — it spreads straight onto
        // Radix — so without this a panel with no subtitle logs Radix's
        // missing-description warning that the dialog it replaces was shielded
        // from.
        {...(props.description ? {} : { 'aria-describedby': undefined })}
        showCloseButton={props.showCloseButton}
        className={mergeClasses(
          'flex flex-col rounded-l-lg sm:max-w-none',
          props.narrow ? 'w-[480px]' : 'w-[640px]',
          // The panel never pads horizontally: each row carries its own inset, so
          // the body spans the panel with its inset inside the clip that would
          // otherwise slice an edge control's focus ring. Ruled off, the header
          // keeps the design system's own 16px so the X it pins at `top-4` lands
          // on the title rather than above it. Unruled, the body adds `2xs` of
          // ring room above and below itself, so the gap is one step short, and so
          // is the bottom padding when the body is the last row.
          props.dividers ? 'gap-0 p-0' : 'gap-lg px-0 py-xl',
          !props.dividers && !props.footer && 'pb-lg',
          props.className,
        )}
      >
        <SheetHeader
          data-testid="form-side-panel-header"
          className={props.dividers ? 'shrink-0 border-b border-border-light' : 'p-0 px-xl'}
        >
          <SheetTitle>{props.title}</SheetTitle>
          {props.description && (
            <SheetDescription data-testid="form-side-panel-description">
              {props.description}
            </SheetDescription>
          )}
        </SheetHeader>

        <div
          data-testid="form-side-panel-body"
          className={clsx(
            'flex min-h-0 flex-1 flex-col overflow-y-auto',
            !props.dividers && 'px-xl py-2xs',
            props.dividers && !props.flushBody && 'p-md',
          )}
        >
          {props.children}
        </div>

        {props.footer && (
          <SheetFooter
            data-testid="form-side-panel-footer"
            className={clsx(
              'flex-row items-center justify-end gap-sm',
              props.dividers ? 'shrink-0 border-t border-border-light' : 'p-0 px-xl',
            )}
          >
            {props.footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
